import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Ellipsis } from 'lucide-react';
import { useApi } from '../../app/providers';
import { href, navigate } from '../../app/router';
import type { Board, Folder, Project } from '../../lib/api';
import { Button, ContextMenu, Dialog, Menu } from '../../ui';
import { cn } from '../../ui/cn';
import { focusRing } from '../../ui/focus';
import { Icon } from '../../ui/icons';
import { useSession } from '../auth/session';
import { ShareDialog } from '../sharing/ShareDialog';
import { SaveTemplateDialog } from '../templates/SaveTemplateDialog';
import { MoveBoardDialog } from './BoardActions';
import { NameDialog } from './dialogs';
import { DuplicateBoardDialog } from './DuplicateBoardDialog';
import {
  boardActions,
  inverseOf,
  projectActions,
  projectInverseOf,
  type ActionState,
  type BoardActionId,
  type Denied,
  type ProjectActionId,
} from './model/actions';
import { useFavorites, useInvalidateContent, useOrg, useProjects } from './queries';
import { useUndoable } from './undo';

/** The ⋯ trigger of a card, a header or a folder (P11 look, now a real menu button). */
export const menuTriggerClass = `grid size-8 shrink-0 cursor-pointer select-none place-items-center rounded-md text-fg-muted os-press hover:bg-surface-2 hover:text-fg data-[popup-open]:bg-surface-2 data-[popup-open]:text-fg pointer-coarse:size-11 ${focusRing}`;

const DENIED_KEY: Record<Denied, string> = {
  notMember: 'dashboard.denied.notMember',
  notCreator: 'dashboard.denied.notCreator',
  archived: 'dashboard.denied.archived',
  notAdmin: 'dashboard.denied.notAdmin',
};

type Entry<T extends string> = { state: ActionState<T>; label: string };

/** Items of one action list, in groups separated by a line; a refused action stays visible, disabled, with its reason. */
function Items<T extends string>({ entries, groups, onAction }: { entries: Entry<T>[]; groups: readonly (readonly T[])[]; onAction: (id: T) => void }) {
  const { t } = useTranslation();
  const blocks = groups.map((g) => entries.filter((e) => g.includes(e.state.id))).filter((b) => b.length > 0);
  return blocks.map((block, i) => (
    <div key={i} role="group">
      {i > 0 && <Menu.Separator />}
      {block.map(({ state, label }) => (
        <Menu.Item
          key={state.id}
          disabled={!state.allowed}
          aria-label={state.allowed ? undefined : `${label} — ${t(DENIED_KEY[state.reason])}`}
          className={state.allowed ? undefined : 'h-auto py-1.5 pointer-coarse:h-auto'}
          onClick={() => onAction(state.id)}
        >
          <span className="flex flex-col">
            <span>{label}</span>
            {!state.allowed && <span className="text-xs text-fg-muted">{t(DENIED_KEY[state.reason])}</span>}
          </span>
        </Menu.Item>
      ))}
    </div>
  ));
}

export type ItemMenu<T extends string> = { entries: Entry<T>[]; groups: readonly (readonly T[])[]; run: (id: T) => void; dialogs: ReactNode; label: string };

/** The ⋯ button and the right-click / long-press menu of one item, both from the same entries. */
export function MenuButton<T extends string>({ menu, triggerLabel }: { menu: ItemMenu<T>; triggerLabel: string }) {
  return (
    <Menu.Root>
      <Menu.Trigger aria-label={triggerLabel} className={cn(menuTriggerClass, 'relative z-10')}>
        <Icon icon={Ellipsis} size={18} />
      </Menu.Trigger>
      <Menu.Popup label={menu.label} align="end">
        <Items entries={menu.entries} groups={menu.groups} onAction={menu.run} />
      </Menu.Popup>
    </Menu.Root>
  );
}

/** Wraps `element` (a card) so a right click or a long press opens the item's menu at the pointer. */
export function WithContextMenu<T extends string>({ menu, element, children }: { menu: ItemMenu<T>; element: ReactElement; children: ReactNode }) {
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger render={element}>{children}</ContextMenu.Trigger>
      <ContextMenu.Popup label={menu.label}>
        <Items entries={menu.entries} groups={menu.groups} onAction={menu.run} />
      </ContextMenu.Popup>
    </ContextMenu.Root>
  );
}

function ErrorLine({ error }: { error: boolean }) {
  const { t } = useTranslation();
  return error ? (
    <p role="alert" className="font-sans text-xs text-danger">
      {t('auth.genericError')}
    </p>
  ) : null;
}

const BOARD_GROUPS: readonly (readonly BoardActionId[])[] = [
  ['open', 'openNewTab'],
  ['rename', 'duplicate', 'move', 'favorite', 'share', 'saveTemplate'],
  ['archive', 'unarchive', 'trash'],
];

/** Everything a board card's menus need: the allowed actions for the signed-in person, how to run them, their dialogs. */
export function useBoardMenu(board: Board): ItemMenu<BoardActionId> {
  const { t } = useTranslation();
  const api = useApi();
  const session = useSession();
  const { org } = useOrg();
  const favorites = useFavorites();
  const projects = useProjects(org && org.id === board.orgId ? org.id : undefined);
  const undoable = useUndoable();
  const [dialog, setDialog] = useState<'rename' | 'move' | 'duplicate' | 'template' | 'share' | null>(null);
  const [error, setError] = useState(false);
  const userId = session.status === 'signed-in' ? session.user.id : '';
  const favorite = favorites.data?.some((b) => b.id === board.id) ?? false;
  const projectArchived = Boolean(projects.data?.find((p) => p.id === board.projectId)?.archivedAt);
  const states = boardActions(board, { org, userId, favorite, canCopy: api.capabilities.boardCopy, projectArchived }).filter((s) => s.id !== 'share' || api.capabilities.sharing);

  const label = (id: BoardActionId): string => {
    if (id === 'favorite') return favorite ? t('dashboard.unfavorite') : t('dashboard.favorite');
    return t(
      {
        open: 'dashboard.open',
        openNewTab: 'dashboard.openNewTab',
        rename: 'dashboard.rename',
        duplicate: 'dashboard.duplicateTo',
        move: 'dashboard.moveTo',
        share: 'dashboard.shareItem',
        saveTemplate: 'boardTemplates.save',
        archive: 'dashboard.archive',
        unarchive: 'dashboard.unarchive',
        trash: 'dashboard.toTrash',
      }[id],
    );
  };

  const guarded = (work: () => Promise<void>) => {
    setError(false);
    work().catch(() => setError(true));
  };

  const run = (id: BoardActionId) => {
    const url = href('board', { boardId: board.id });
    switch (id) {
      case 'open':
        return navigate(url);
      case 'openNewTab':
        return void window.open(url, '_blank', 'noopener');
      case 'favorite':
        return guarded(() =>
          undoable(() => api.setFavorite(board.id, !favorite), [inverseOf('favorite', board, { favorite: !favorite })!], favorite ? t('dashboard.done.unfavorite') : t('dashboard.done.favorite')),
        );
      case 'archive':
      case 'unarchive':
      case 'trash':
        return guarded(() => undoable(() => api.lifecycle('board', id, board.id), [inverseOf(id, board)!], t(`dashboard.done.${id}`)));
      case 'rename':
      case 'move':
      case 'duplicate':
      case 'share':
        return setDialog(id);
      case 'saveTemplate':
        return setDialog('template');
    }
  };

  const close = () => setDialog(null);
  const dialogs = (
    <>
      <ErrorLine error={error} />
      {dialog === 'rename' && (
        <NameDialog
          title={t('dashboard.renameBoard')}
          label={t('dashboard.boardTitle')}
          submitLabel={t('dashboard.save')}
          initial={board.title}
          max={200}
          onClose={close}
          onSubmit={(title) => undoable(() => api.updateBoard(board.id, { title }), [inverseOf('rename', board)!], t('dashboard.done.rename'))}
        />
      )}
      {dialog === 'move' && org && <MoveBoardDialog boards={[board]} org={org} onClose={close} />}
      {dialog === 'duplicate' && org && <DuplicateBoardDialog board={board} org={org} onClose={close} />}
      {dialog === 'template' && <SaveTemplateDialog board={board} onClose={close} />}
      {dialog === 'share' && <ShareDialog scope={{ type: 'board', id: board.id }} title={board.title} onClose={close} />}
    </>
  );

  return { entries: states.map((state) => ({ state, label: label(state.id) })), groups: BOARD_GROUPS, run, dialogs, label: t('dashboard.boardActions') };
}

const PROJECT_GROUPS: readonly (readonly ProjectActionId[])[] = [['open'], ['rename', 'share'], ['archive', 'unarchive', 'trash']];

export function useProjectMenu(project: Project): ItemMenu<ProjectActionId> {
  const { t } = useTranslation();
  const api = useApi();
  const session = useSession();
  const { org } = useOrg();
  const invalidate = useInvalidateContent();
  const undoable = useUndoable();
  const [dialog, setDialog] = useState<'rename' | 'share' | null>(null);
  const [error, setError] = useState(false);
  const userId = session.status === 'signed-in' ? session.user.id : '';
  const states = projectActions(project, { org, userId }).filter((s) => s.id !== 'share' || api.capabilities.sharing);

  const label = (id: ProjectActionId) =>
    t({ open: 'dashboard.open', rename: 'dashboard.rename', share: 'dashboard.shareItem', archive: 'dashboard.archive', unarchive: 'dashboard.unarchive', trash: 'dashboard.toTrash' }[id]);

  const run = (id: ProjectActionId) => {
    if (id === 'open') return navigate(href('project', { projectId: project.id }));
    if (id === 'rename' || id === 'share') return setDialog(id);
    setError(false);
    undoable(() => api.lifecycle('project', id, project.id), [projectInverseOf(id, project)!], t(`dashboard.done.project.${id}`))
      .then(() => {
        // Trashing the project whose page is open goes home (Undo stays offered there).
        if (id === 'trash' && window.location.pathname.startsWith(href('project', { projectId: project.id }))) navigate(href('home'));
      })
      .catch(() => setError(true));
  };

  const close = () => setDialog(null);
  const dialogs = (
    <>
      <ErrorLine error={error} />
      {dialog === 'rename' && (
        <NameDialog
          title={t('dashboard.renameProject')}
          label={t('dashboard.projectName')}
          submitLabel={t('dashboard.save')}
          initial={project.name}
          max={120}
          onClose={close}
          onSubmit={async (name) => {
            await api.updateProject(project.id, { name });
            await invalidate();
          }}
        />
      )}
      {dialog === 'share' && <ShareDialog scope={{ type: 'project', id: project.id }} title={project.name} onClose={close} />}
    </>
  );

  return { entries: states.map((state) => ({ state, label: label(state.id) })), groups: PROJECT_GROUPS, run, dialogs, label: t('dashboard.projectActions') };
}

type FolderActionId = 'rename' | 'delete';

/** Folder menu: rename, delete after a confirmation (its boards move to the project root, as the SQL foreign key does). */
export function useFolderMenu(folder: Folder): ItemMenu<FolderActionId> {
  const { t } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateContent();
  const [dialog, setDialog] = useState<FolderActionId | null>(null);
  const [error, setError] = useState(false);
  const close = () => setDialog(null);

  const remove = async () => {
    setError(false);
    try {
      await api.deleteFolder(folder.id);
      await invalidate();
      close();
    } catch {
      setError(true);
    }
  };

  const dialogs = (
    <>
      {!dialog && <ErrorLine error={error} />}
      {dialog === 'rename' && (
        <NameDialog
          title={t('dashboard.renameFolder')}
          label={t('dashboard.folderName')}
          submitLabel={t('dashboard.save')}
          initial={folder.name}
          max={120}
          onClose={close}
          onSubmit={async (name) => {
            await api.renameFolder(folder.id, name);
            await invalidate();
          }}
        />
      )}
      {dialog === 'delete' && (
        <Dialog
          title={t('dashboard.deleteFolder')}
          description={t('dashboard.deleteFolderHint', { name: folder.name })}
          onClose={close}
          footer={
            <>
              <Button onClick={close}>{t('dashboard.cancel')}</Button>
              <Button variant="danger" onClick={() => void remove()}>
                {t('dashboard.deleteFolder')}
              </Button>
            </>
          }
        >
          <ErrorLine error={error} />
        </Dialog>
      )}
    </>
  );

  return {
    entries: [
      { state: { id: 'rename', allowed: true }, label: t('dashboard.renameFolder') },
      { state: { id: 'delete', allowed: true }, label: t('dashboard.deleteFolder') },
    ],
    groups: [['rename'], ['delete']],
    run: setDialog,
    dialogs,
    label: t('dashboard.folderActions'),
  };
}
